import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';
import { validateResourceUrl } from '../utils/validation.js';

/**
 * 快应用背景移除请求参数
 */
export interface QuickAppBackgroundRemoverStandardParams {
  /**
   * 图像URL，必须是有效的HTTP/HTTPS URL
   */
  imageUrl: string;
  /**
   * 回调URL（可选），必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
}

/**
 * 快应用背景移除生成的资源
 */
export interface QuickAppBackgroundRemoverStandardResource {
  /**
   * 资源类型（如 'image'）
   */
  type: string;
  /**
   * 资源URL
   */
  url: string;
  /**
   * 资源大小（字节）
   */
  size?: number;
  /**
   * 任务ID
   */
  jobId?: string;
  /**
   * 其他扩展字段
   */
  [key: string]: any;
}

/**
 * quickAppBackgroundRemoverStandard
 *
 * 快应用标准背景移除
 *
 * 使用快应用（Quick App）模板进行图像背景移除。
 * 基于 ComfyUI 工作流，提供高质量的背景移除效果。
 *
 * Features:
 * - 自动背景移除
 * - 支持各种图像格式
 * - 异步任务处理，自动轮询直到完成
 *
 * @example
 * ```typescript
 * import { initSeacloud, quickAppBackgroundRemoverStandard } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const resources = await quickAppBackgroundRemoverStandard({
 *   imageUrl: 'https://example.com/photo.jpg'
 * });
 *
 * console.log(resources[0].url); // 处理后的图片URL
 * ```
 *
 * @param params 背景移除参数
 * @returns 生成的资源列表（包含移除背景后的图像）
 */
export async function quickAppBackgroundRemoverStandard(
  params: QuickAppBackgroundRemoverStandardParams,
): Promise<QuickAppBackgroundRemoverStandardResource[]> {
  const client = await getClient();

  // 构建快应用 inputs
  const inputs: { field: string; value: any }[] = [
    { field: 'image', value: params.imageUrl }
  ];

  if (params.callback) {
    inputs.push({ field: 'callback', value: params.callback });
  }

  // 配置轮询选项
  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 120,
  };

  // 调用快应用生成任务
  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'comfyui',
      input: [{
        params: {
          template_id: 'cu777cde878c73d4ks80',
          inputs: inputs
        }
      }],
    },
    pollingOptions,
  );

  // 提取并验证资源
  const resources: QuickAppBackgroundRemoverStandardResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          validateResourceUrl(resource, 'image');
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
